import { useEffect, useState } from 'react';
import { db } from '@/lib/db';
import type { Course, CourseHole, CourseTee, Game, GameParticipant, Member, MemberPublic } from '@/lib/db/types';
import { settleGame } from '@/lib/games/settlement';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';
import { InvitePlayersPanel } from '@/components/shared/InvitePlayersPanel';
import { OrganizerActions } from '@/components/shared/OrganizerActions';
import { findGameFormat } from '@/lib/reference/golf-games';
import { GameResultModal } from '@/components/games/GameResultModal';
import { GroupScorecard } from '@/components/play/GroupScorecard';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

interface Props {
  gameId: string;
  member: Member;
  courseNames: Record<string, string>;
  onClose: () => void;
  onUpdated: () => void;
}

type Tab = 'scorecard' | 'players';

export function GameDetailSheet({ gameId, member, courseNames, onClose, onUpdated }: Props) {
  const [game, setGame] = useState<Game | null>(null);
  const [participants, setParticipants] = useState<GameParticipant[]>([]);
  const [memberMap, setMemberMap] = useState<Record<string, MemberPublic>>({});
  const [courseHoles, setCourseHoles] = useState<CourseHole[]>([]);
  const [course, setCourse] = useState<Course | null>(null);
  const [tee, setTee] = useState<CourseTee | null>(null);
  const [holeScores, setHoleScores] = useState<Record<string, (number | null)[]>>({});
  const [tab, setTab] = useState<Tab>('scorecard');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [settlement, setSettlement] = useState<Record<string, unknown> | null>(null);
  const [inviteIds, setInviteIds] = useState<string[]>([]);
  const [guestDraft, setGuestDraft] = useState('');
  const [guestDrafts, setGuestDrafts] = useState<Array<{ name: string }>>([]);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [transferTo, setTransferTo] = useState('');
  const [allMembers, setAllMembers] = useState<MemberPublic[]>([]);

  const reload = async () => {
    const [g, parts, people] = await Promise.all([
      db.games.get(gameId),
      db.games.participants(gameId),
      db.members.list({ limit: 100 }),
    ]);
    setGame(g);
    setParticipants(parts);
    setMemberMap(Object.fromEntries(people.map((m) => [m.id, m])));
    setAllMembers(people.filter((m) => m.id !== member.id));
    if (g) {
      const [holes, courses, tees] = await Promise.all([
        db.courses.holes(g.course_id),
        db.courses.list(),
        g.tee_id ? db.courses.tees(g.course_id) : Promise.resolve([] as CourseTee[]),
      ]);
      setCourseHoles(holes);
      setCourse(courses.find((c) => c.id === g.course_id) ?? null);
      setTee(g.tee_id ? tees.find((t) => t.id === g.tee_id) ?? null : null);
      const saved = (g.config?.scores || {}) as Record<string, (number | null)[]>;
      const init: Record<string, (number | null)[]> = {};
      parts.forEach((p) => {
        const key = p.member_id || p.guest_name || '';
        init[key] = saved[key] || Array(18).fill(null);
      });
      setHoleScores(init);
      if (g.config?.settlement) setSettlement(g.config.settlement as Record<string, unknown>);
    }
    setLoading(false);
  };

  useEffect(() => { void reload(); }, [gameId]);

  const participantKeys = participants.map((p) => p.member_id || p.guest_name || '').filter(Boolean);

  const setHoleScore = (key: string, holeIdx: number, val: number | null) => {
    setHoleScores((prev) => {
      const arr = [...(prev[key] || Array(18).fill(null))];
      arr[holeIdx] = val;
      return { ...prev, [key]: arr };
    });
  };

  const saveScores = async () => {
    if (!game) return;
    await db.games.updateConfig(game.id, { ...game.config, scores: holeScores });
  };

  const join = async () => {
    setActing(true);
    setJoinError(null);
    try {
      await db.games.join(gameId);
      await reload();
      onUpdated();
    } catch (err) {
      setJoinError((err as Error).message || 'Could not join game');
    } finally {
      setActing(false);
    }
  };

  const start = async () => {
    if (!game) return;
    setActing(true);
    try {
      await db.games.start(game.id);
      await reload();
      onUpdated();
    } finally {
      setActing(false);
    }
  };

  const complete = async () => {
    if (!game) return;
    setActing(true);
    try {
      await saveScores();
      const scoring = (game.config?.scoring as 'net' | 'gross') || 'net';
      const handicapAllowance = game.config?.handicapAllowance as number | undefined;
      const result = settleGame(
        game.format_id,
        participants,
        holeScores,
        memberMap,
        courseHoles,
        game.wager_cents,
        scoring,
        handicapAllowance,
      );
      const results = participants.map((p, i) => {
        const key = p.member_id || p.guest_name || '';
        const grossArr = holeScores[key] || [];
        const gross = grossArr
          .filter((s): s is number => Number.isFinite(s))
          .reduce((sum, b) => sum + b, 0);
        const netCents = result?.net[key] != null ? Math.round(result.net[key] * 100) : 0;
        return {
          participantKey: key,
          final_score: gross,
          net_score: gross,
          net_won_cents: netCents,
          position: i + 1,
        };
      }).sort((a, b) => a.final_score - b.final_score)
        .map((r, i) => ({ ...r, position: i + 1 }));

      const settlementData: Record<string, unknown> = result ? { ...result } : {};
      setSettlement(settlementData);
      await db.games.settle(gameId, results, settlementData);
      await reload();
      onUpdated();
      setShowResult(true);
    } finally {
      setActing(false);
    }
  };

  const shareResult = async () => {
    if (!game) return;
    const format = findGameFormat(game.format_id);
    await db.posts.create({
      type: 'round',
      game_id: game.id,
      body: `Game complete: ${game.name || format?.name}`,
      metadata: {
        games: [{
          type: game.format_id.includes('skin') ? 'skins' : 'nassau',
          format_id: game.format_id,
          scoring: (game.config?.scoring as 'net' | 'gross') || 'net',
          amount: Math.round(game.wager_cents / 100) || 5,
          opponentIds: participants.filter((p) => p.member_id && p.member_id !== member.id).map((p) => p.member_id as string),
          settlement: (settlement || game.config?.settlement) as Record<string, unknown> | undefined,
        }],
        courseName: courseNames[game.course_id],
      },
    });
    setShowResult(false);
    onClose();
    onUpdated();
  };

  const donate = async (amountCents: number) => {
    await db.donations.log({ amount_cents: amountCents, game_id: gameId });
  };

  const sendInvites = async () => {
    if (!inviteIds.length && !guestDrafts.length) return;
    setActing(true);
    try {
      await db.games.addParticipants(gameId, {
        members: inviteIds,
        guests: guestDrafts,
      });
      setInviteIds([]);
      setGuestDrafts([]);
      setGuestDraft('');
      await reload();
      onUpdated();
    } finally {
      setActing(false);
    }
  };

  const confirmRemoveGame = async () => {
    setActing(true);
    try {
      await db.games.cancel(gameId);
      setShowDeleteConfirm(false);
      onUpdated();
      onClose();
    } finally {
      setActing(false);
    }
  };

  const transferGame = async () => {
    if (!transferTo) return;
    setActing(true);
    try {
      await db.games.transferOrganizer(gameId, transferTo);
      await reload();
      onUpdated();
      setTransferTo('');
    } finally {
      setActing(false);
    }
  };

  const alreadyInGame = new Set(participants.map((p) => p.member_id).filter(Boolean));
  const invitableMembers = allMembers.filter((m) => !alreadyInGame.has(m.id));

  if (loading || !game) {
    return <Sheet title="Game" onClose={onClose}><div className="p-4 text-sm text-mute">Loading…</div></Sheet>;
  }

  const format = findGameFormat(game.format_id);
  const isOrganizer = game.organizer_id === member.id;
  const isParticipant = participants.some((p) => p.member_id === member.id);
  const maxPlayers = 4;
  const canJoin = !isOrganizer
    && !isParticipant
    && (game.status === 'setup' || game.status === 'active')
    && Boolean(game.config?.is_joinable)
    && !game.track_stakes
    && participants.length < maxPlayers;
  const openSpots = maxPlayers - participants.length;
  const teeName = typeof game.config?.teeName === 'string' ? game.config.teeName : (tee?.name || '—');
  const holesLabel = game.holes === 'full' ? '18 holes' : game.holes === 'front-9' ? 'Front 9' : 'Back 9';
  const wagerDisplay = game.track_stakes && game.wager_cents > 0
    ? `$${(game.wager_cents / 100).toFixed(0)}`
    : '—';

  if (showResult) {
    return (
      <GameResultModal
        game={game}
        participants={participants}
        members={memberMap}
        courseName={courseNames[game.course_id]}
        currentMember={member}
        onClose={() => { setShowResult(false); onClose(); }}
        onShare={() => void shareResult()}
        onDonate={(c) => void donate(c)}
      />
    );
  }

  return (
    <>
    <Sheet title={game.name || format?.name || 'Game'} onClose={onClose}>
      <div className="space-y-4 px-4 pb-8 pt-2">
        <div className="rounded-xl bg-cream p-3.5">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold tracking-wide text-mute">WAGER</div>
              <div className="font-serif text-lg font-bold text-navy">{wagerDisplay}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] font-bold tracking-wide text-mute">PLAYERS</div>
              <div className="font-serif text-lg font-bold text-navy">{participants.length}/{maxPlayers}</div>
            </div>
          </div>
          <div className="text-xs leading-snug text-ink-soft">
            <strong>{courseNames[game.course_id] || 'Course'}</strong> · {teeName} tees · {holesLabel}
          </div>
        </div>

        <div className="flex gap-1.5">
          {(['scorecard', 'players'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`flex-1 rounded-lg py-2 text-xs font-bold capitalize ${
                tab === t ? 'bg-navy text-cream shadow-bt' : 'border border-line bg-paper text-ink-soft'
              }`}
            >
              {t === 'players' && openSpots > 0 && game.status === 'setup' ? `Invite (${openSpots})` : t}
            </button>
          ))}
        </div>

        {tab === 'players' && isOrganizer && game.status === 'setup' && (
          <div className="space-y-3">
            <InvitePlayersPanel
              members={invitableMembers}
              selectedIds={inviteIds}
              onToggle={(id) => setInviteIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
              guests={guestDrafts}
              guestDraft={guestDraft}
              onGuestDraftChange={setGuestDraft}
              onAddGuest={() => {
                if (!guestDraft.trim()) return;
                setGuestDrafts((g) => [...g, { name: guestDraft.trim() }]);
                setGuestDraft('');
              }}
              onRemoveGuest={(i) => setGuestDrafts((g) => g.filter((_, idx) => idx !== i))}
            />
            <button type="button" disabled={acting || (!inviteIds.length && !guestDrafts.length)} onClick={() => void sendInvites()} className="w-full rounded-full bg-navy py-2.5 text-sm font-bold text-cream disabled:opacity-50">
              {acting ? 'Inviting…' : 'Send Invites'}
            </button>
          </div>
        )}

        {tab === 'players' && (
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-mute">Players ({participants.length})</div>
            {participants.map((p) => {
              const key = p.member_id || p.guest_name || '';
              const person = p.member_id ? memberMap[p.member_id] : null;
              return (
                <div key={key} className="mb-2 flex items-center gap-3 rounded-xl bg-cream p-2">
                  {person ? <Avatar member={person} size={32} /> : <div className="h-8 w-8 rounded-full bg-line" />}
                  <span className="flex-1 text-sm font-semibold text-navy">{person?.name || p.guest_name}</span>
                  {game.status === 'completed' && p.final_score != null && (
                    <span className="font-mono font-bold text-navy">{p.final_score}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'scorecard' && game.status === 'active' && (
          <div className="space-y-3">
            <GroupScorecard
              holes={courseHoles.filter((h) => {
                if (game.holes === 'front-9') return h.hole_num <= 9;
                if (game.holes === 'back-9') return h.hole_num > 9;
                return true;
              })}
              course={course}
              teeSlope={tee?.slope}
              teeYardageTotal={tee?.yardage_total}
              players={participants.map((p) => {
                const person = p.member_id ? memberMap[p.member_id] : null;
                return {
                  id: p.member_id || p.guest_name || '',
                  name: person?.name || p.guest_name || 'Guest',
                  handicap: person?.handicap ?? p.guest_handicap,
                  you: p.member_id === member.id,
                  guest: !p.member_id,
                  avatar: person?.avatar,
                  profile_photo_url: person?.profile_photo_url,
                };
              })}
              scoresById={holeScores}
              onScore={(key, hi, val) => setHoleScore(key, hi, val)}
            />
            <button type="button" onClick={() => void saveScores()} className="w-full rounded-full border border-line py-2 text-xs font-bold text-navy">
              Save scores
            </button>
          </div>
        )}

        {tab === 'scorecard' && game.status !== 'active' && (
          <p className="text-sm text-ink-soft">{game.status === 'setup' ? 'Start the game to enter hole-by-hole scores.' : 'Game complete.'}</p>
        )}

        {isOrganizer && game.status === 'setup' && (
          <button type="button" disabled={acting} onClick={() => void start()} className="w-full rounded-full bg-gold py-3 text-sm font-bold text-navy disabled:opacity-50">Start Game</button>
        )}
        {canJoin && (
          <>
            {joinError && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{joinError}</div>
            )}
            <button type="button" disabled={acting} onClick={() => void join()} className="w-full rounded-full bg-navy py-3 text-sm font-bold text-cream disabled:opacity-50">
              {acting ? 'Joining…' : game.status === 'active' ? 'Join live game' : 'Join Game'}
            </button>
          </>
        )}
        {isOrganizer && game.status === 'active' && (
          <button type="button" disabled={acting} onClick={() => void complete()} className="w-full rounded-full bg-gold py-3 text-sm font-bold text-navy disabled:opacity-50">{acting ? 'Settling…' : 'Complete & Settle'}</button>
        )}
        {game.status === 'completed' && (
          <button type="button" onClick={() => setShowResult(true)} className="w-full rounded-full border border-line py-3 text-sm font-semibold text-navy">View Results</button>
        )}
        {isOrganizer && game.status !== 'cancelled' && (
          <OrganizerActions
            members={allMembers}
            currentMemberId={member.id}
            transferTo={transferTo}
            onTransferToChange={setTransferTo}
            onTransfer={() => void transferGame()}
            onDelete={() => setShowDeleteConfirm(true)}
            deleteLabel="Delete Game"
            transferBusy={acting}
            deleteBusy={acting}
          />
        )}
      </div>
    </Sheet>
    <ConfirmDialog
      open={showDeleteConfirm}
      title="Delete this game?"
      message="It will be cancelled and hidden from active lists."
      confirmLabel="Delete"
      destructive
      busy={acting}
      onConfirm={() => void confirmRemoveGame()}
      onCancel={() => setShowDeleteConfirm(false)}
    />
    </>
  );
}
